import { useState } from "react";
import { supabase } from "@/lib/supabase";
import {
  deletePageRecordings,
  RecordingStorageDeletionError,
} from "@/lib/recordingLifecycle";

type DeleteRecordingControlProps = {
  userId: string;
  pageNumber: string;
  onDeleted: () => void;
};

export default function DeleteRecordingControl({
  userId,
  pageNumber,
  onDeleted,
}: DeleteRecordingControlProps) {
  const [confirming, setConfirming] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleDelete() {
    if (!supabase) {
      setError("Your recording service isn’t available. Please try again.");
      return;
    }
    setIsDeleting(true);
    setError(null);
    try {
      await deletePageRecordings(supabase, userId, pageNumber);
      setConfirming(false);
      onDeleted();
    } catch (deleteError) {
      setError(deleteError instanceof RecordingStorageDeletionError
        ? "The audio file couldn’t be deleted. Your recording is still saved. Try again."
        : "We couldn’t finish deleting this recording. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="delete-recording-control">
      {error && <p className="delete-recording-error" role="alert">{error}</p>}
      {!confirming ? (
        <button
          type="button"
          className="secondary-action delete-recording-trigger"
          onClick={() => { setError(null); setConfirming(true); }}
        >
          Delete this recording
        </button>
      ) : (
        <div className="delete-recording-confirm" role="group" aria-label="Confirm recording deletion">
          <p>This permanently deletes the audio and turns off its share link.</p>
          <div className="delete-recording-actions">
            <button type="button" className="destructive-action" onClick={() => void handleDelete()} disabled={isDeleting}>
              {isDeleting ? "Deleting…" : "Delete permanently"}
            </button>
            <button type="button" className="secondary-action" onClick={() => { setConfirming(false); setError(null); }} disabled={isDeleting}>
              Keep recording
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
